import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  CollectionsResponse,
  Collection,
  Item,
  Bucket,
  SearchResponse,
  SearchPayload,
  AggregationResponse
  // AggregationPayload
} from 'stac-types';
import type { Geometry } from 'geojson';
import { ZOOM_LEVELS } from '../utils/constants.ts';
// import { RootState } from 'store';
// import { STAC } from 'stac-js';

const searchApi_URL = process.env.REACT_APP_SEARCH_API;

// Define a service using a base URL and expected endpoints
export const searchApi = createApi({
  reducerPath: 'searchApi',
  baseQuery: fetchBaseQuery({
    baseUrl: searchApi_URL,
    timeout: 15000, // Set a default timeout of 15 seconds
    prepareHeaders: (headers, { getState }) => {
      // const token = (getState() as RootState).auth.authToken;
      // if (token) {
      //   headers.set('Authorization', `Bearer ${token}`);
      // }
      return headers;
    }
  }),
  endpoints: (builder) => ({
    getCollectionsResponse: builder.query<CollectionsResponse, void>({
      query: () => `collections`
    }),
    getCollectionByCollectionID: builder.query<Collection, string>({
      query: (collectionID) => `collections/${collectionID}`
    }),
    getCollectionQueryablesByCollectionID: builder.query<
      Record<string, any>,
      string
    >({
      query: (collectionID) => `collections/${collectionID}/queryables`
    }),
    getCollectionItemsByCollectionID: builder.query<
      SearchResponse,
      {
        collectionID: string;
        page: number;
        numResultsPerPage: number;
      }
    >({
      query: ({ collectionID, page = 1, numResultsPerPage = 20 }) =>
        `collections/${collectionID}/items?page=${page}&limit=${numResultsPerPage}`
    }),
    getItemByCollectionIDAndItemID: builder.query<
      Item,
      { collectionID: string; itemID: string }
    >({
      query: ({ collectionID, itemID }) =>
        `collections/${collectionID}/items/${itemID}`
    }),
    getItemDownloadByCollectionIDAndItemID: builder.query<
      Item,
      { collectionID: string; itemID: string }
    >({
      query: ({ collectionID, itemID }) =>
        `collections/${collectionID}/items/${itemID}/download`
    }),

    searchItems: builder.query<SearchResponse, SearchPayload>({
      query: ({ searchQueryParameters }) => ({
        url: 'search',
        method: 'POST',
        body: searchQueryParameters
      })
    }),
    getSearchResultSpatialExtent: builder.query<Geometry, SearchPayload>({
      query: ({ searchQueryParameters }) => ({
        url: 'aggregate',
        method: 'POST',
        body: {
          ...searchQueryParameters,
          aggregations: ['geometry_unaryunion']
        }
      }),
      transformResponse: (response: AggregationResponse) => {
        if (response.aggregations?.[0]?.value) {
          return response.aggregations[0].value as Geometry;
        }
        return null;
      }
    }),
    getSearchResultTemporalFrequency: builder.query<Bucket[], SearchPayload>({
      query: ({ searchQueryParameters, zoomLevel = ZOOM_LEVELS.month }) => {
        // bound zoomlevel to the range in this endpoint
        const zoomLevelRange = [ZOOM_LEVELS.month, ZOOM_LEVELS.year];
        const boundedZoomLevel = Math.min(
          Math.max(zoomLevel, zoomLevelRange[0]),
          zoomLevelRange[1]
        );
        // format zoomlevel to string
        const temporalGranularity = Object.entries(ZOOM_LEVELS).find(
          ([_, value]) => value === boundedZoomLevel
        )?.[0];
        return {
          url: 'aggregate',
          method: 'POST',
          body: {
            ...searchQueryParameters,
            aggregations: [`${temporalGranularity}_frequency`]
          }
        };
      },
      transformResponse: (response: AggregationResponse) => {
        if (response.aggregations?.[0]?.buckets) {
          return response.aggregations[0].buckets;
        }
        return null;
      }
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetCollectionsResponseQuery,
  useGetCollectionQueryablesByCollectionIDQuery,
  useGetCollectionItemsByCollectionIDQuery,
  useGetItemByCollectionIDAndItemIDQuery,
  useGetItemDownloadByCollectionIDAndItemIDQuery,
  useSearchItemsQuery,
  useLazySearchItemsQuery,
  useLazyGetSearchResultSpatialExtentQuery,
  useLazyGetSearchResultTemporalFrequencyQuery,
  useGetSearchResultTemporalFrequencyQuery
} = searchApi;
